import type { FC } from 'react';
import React from 'react';

import type { SheetProps } from '@sravni/react-design-system/dist/types/components/Sheet';
import { Button } from '@sravni/react-design-system/lib/Button';
import { Dialog } from '@sravni/react-design-system/lib/Dialog';
import { useIsMobile } from '@sravni/react-utils';

import type { IClientMicrocreditListItem } from '@src/@types/clienttypes';
import type { AffSub4 } from '@src/@types/microcredits';
import { PIXELS_TYPES } from '@src/constants/analytics';
import { useSelectUser } from '@src/reducers/user';
import { useAdvertisingPixel } from 'app/providers';
import { FullDealCalculatorCalculationsMinimal } from 'entities/FullDeal/ui/FullDealCalculationsMinimal/FullDealCalculationsMinimal';
import { Authentication } from 'features/Authentication/ui/Authentication';
import { buildHasOfferLinkForFullDeal } from 'features/FullDeal/lib';
import { FullDealEntryPointCompose } from 'widgets/FullDealEntryPoint/ui/FullDealEntryPointCompose/FullDealEntryPointCompose';

import styles from './FullDealCalculatorWithAuthSheet.module.scss';

interface Props extends Pick<SheetProps, 'visible' | 'onClose'> {
  data: { heading?: string; offer: IClientMicrocreditListItem; position: number };
}

export const FullDealCalculatorWithAuthSheet: FC<Props> = ({ visible, onClose, data: { heading, offer } }) => {
  const isMobile = useIsMobile();
  const { getPixelLink } = useAdvertisingPixel();

  const user = useSelectUser();

  const handleAuthorizedHandler =
    ({ amount, term }: { amount: number; term: number }) =>
    () => {
      const affSub4ForMfoButton: AffSub4 = {
        organizationName: offer.organization.name,
        productName: offer.name,
        organizationId: offer.organization._id,
        productId: offer._id,
      };

      const sourceLink = buildHasOfferLinkForFullDeal({
        link: offer?.advertising?.monetization?.pixels?.click,
        offerId: offer.advertising.offerId,
        amount,
        term,
      });

      const source = ['search', `position_${offer?.advertising?.position}`].join('|');
      const targetLink = getPixelLink(sourceLink, {
        sub: offer.advertising.advSub,
        affSub4: affSub4ForMfoButton,
        source,
        pixelType: PIXELS_TYPES.CLICK,
      });

      window.location.href = targetLink;
    };

  return (
    <Dialog
      className={styles.dialog}
      visible={visible}
      onClose={onClose}
      // @ts-expect-error dragElastic не описан в типах библиотеки
      dragElastic={false}
      fullscreen={isMobile}
      closable={false}
      closeOnBackward
    >
      <Dialog.Content className={styles.sheetContent}>
        <FullDealEntryPointCompose
          offer={offer}
          heading={heading}
          highlightOffer={false}
          renderActions={(props) =>
            !user ? (
              <Authentication
                productId={offer.advertising.offerId}
                onAuthorized={handleAuthorizedHandler(props)}
                content={<FullDealCalculatorCalculationsMinimal {...props} />}
              />
            ) : (
              <Button variant="primary" block onClick={handleAuthorizedHandler(props)}>
                Продолжить
              </Button>
            )
          }
        />
      </Dialog.Content>
    </Dialog>
  );
};

FullDealCalculatorWithAuthSheet.displayName = 'FullDealCalculatorWithAuthSheet';
